var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useGetOne, useNotify } from "react-admin";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, CircularProgress, Drawer, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ChannelLabel, Dialog } from "@sequentech/ui-essentials";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useMessagingAccounts } from "@/hooks/useMessagingAccounts";
import { IPermissions } from "@/types/keycloak";
import { EMessagePurpose, MESSAGE_CHANNELS, MESSAGE_PURPOSES, } from "@/types/messaging";
import { accountSenderLabel, accountUnsupported, checkedForAnotherIdentity, } from "@/services/messaging";
import { actionRefusalReason, reasonText } from "@/services/messagingReasons";
import { CHECK_MESSAGING_ACCOUNT } from "@/queries/CheckMessagingAccount";
import { DELETE_MESSAGING_ACCOUNT } from "@/queries/DeleteMessagingAccount";
import { accountReadiness } from "./messagingAccountForm";
import { MESSAGING_ACCOUNT_EDITOR_TITLE, MessagingAccountEditor, formatDate, } from "./MessagingAccountEditor";
import { MessagingTestDialog } from "./MessagingTestDialog";
import { MessagingTabs } from "./SentMessages/MessagingTabs";
import { MessagingMonthlyVolumes } from "./MessagingMonthlyVolumes";
var WRITE_ROLE = { headers: { "x-hasura-role": IPermissions.MESSAGING_ACCOUNT_WRITE } };
/** A readiness chip with the reasons it is not ready below it. */
export var ReadinessCell = function (_a) {
    var ready = _a.ready, label = _a.label, reasons = _a.reasons;
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-start" }, children: [_jsx(Chip, { size: "small", color: ready ? "success" : "default", variant: ready ? "filled" : "outlined", label: label }), reasons.map(function (reason) { return (_jsx(Typography, { variant: "caption", color: "text.secondary", children: reason }, reason)); })] }));
};
/** Accounts in channel order, then by name. */
export var sortAccounts = function (accounts) {
    return __spreadArray([], accounts, true).sort(function (a, b) {
        return MESSAGE_CHANNELS.indexOf(a.channel) - MESSAGE_CHANNELS.indexOf(b.channel) ||
            a.name.localeCompare(b.name);
    });
};
/** The Accounts tab: the tenant's sending accounts. */
var MessagingAccountList = function () {
    var _a, _b, _c, _d;
    var _e = useTranslation(), t = _e.t, i18n = _e.i18n;
    var notify = useNotify();
    var authContext = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var canWrite = authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_ACCOUNT_WRITE);
    var _f = useMessagingAccounts({
        callbackStatus: true,
    }), unsorted = _f.accounts, loading = _f.loading, error = _f.error, refetch = _f.refetch;
    var accounts = useMemo(function () { return sortAccounts(unsorted); }, [unsorted]);
    var tenant = useGetOne("sequent_backend_tenant", { id: tenantId }, { enabled: !!tenantId }).data;
    var languages = (_c = (_b = (_a = tenant === null || tenant === void 0 ? void 0 : tenant.settings) === null || _a === void 0 ? void 0 : _a.language_conf) === null || _b === void 0 ? void 0 : _b.enabled_language_codes) !== null && _c !== void 0 ? _c : [];
    var _g = useState({ kind: "CLOSED" }), drawer = _g[0], setDrawer = _g[1];
    var _h = useState(null), testing = _h[0], setTesting = _h[1];
    var _j = useState(null), deleting = _j[0], setDeleting = _j[1];
    var _k = useState(null), checking = _k[0], setChecking = _k[1];
    var checkAccount = useMutation(CHECK_MESSAGING_ACCOUNT, { context: WRITE_ROLE })[0];
    var deleteAccount = useMutation(DELETE_MESSAGING_ACCOUNT, { context: WRITE_ROLE })[0];
    var reload = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, refetch()];
                case 1:
                    _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    notify(t("messagingAccounts.list.loadError"), { type: "error" });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var check = function (account) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setChecking(account.id);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, checkAccount({ variables: { id: account.id } })];
                case 2:
                    _b.sent();
                    notify(t("messagingAccounts.check.done", { name: account.name }), { type: "info" });
                    return [4 /*yield*/, reload()];
                case 3:
                    _b.sent();
                    return [3 /*break*/, 6];
                case 4:
                    _a = _b.sent();
                    notify(t("messagingAccounts.check.error", { name: account.name }), { type: "error" });
                    return [3 /*break*/, 6];
                case 5:
                    setChecking(null);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var remove = function (account) { return __awaiter(void 0, void 0, void 0, function () {
        var error_1, reason;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, deleteAccount({ variables: { id: account.id } })];
                case 1:
                    _a.sent();
                    notify(t("messagingAccounts.delete.success"), { type: "success" });
                    return [4 /*yield*/, reload()];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    reason = actionRefusalReason(error_1);
                    notify(reason
                        ? t("messagingAccounts.delete.refused", { reason: reasonText(t, reason) })
                        : t("messagingAccounts.delete.error"), { type: "error" });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    // A new account with a callback is shown again once saved, with its callback URL.
    var created = function (id, callback) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, reload()];
                case 1:
                    _a.sent();
                    setDrawer(callback ? { kind: "CREATED", id: id, callback: callback } : { kind: "CLOSED" });
                    return [2 /*return*/];
            }
        });
    }); };
    var drawerAccount = drawer.kind === "EDIT" || drawer.kind === "CREATED"
        ? accounts.find(function (account) { return account.id === drawer.id; })
        : undefined;
    var blockerLabels = function (blockers) {
        return blockers.map(function (blocker) { return t("messaging.blocker.".concat(blocker)); });
    };
    var purposeLabel = function (purpose) {
        return purpose === EMessagePurpose.OTP
            ? t("messaging.readiness.readyOtp")
            : t("messaging.readiness.readyNotice");
    };
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 2, padding: 2 }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { flex: 1, minWidth: 0 }, children: t("messagingAccounts.description") }), canWrite && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: function () { return setDrawer({ kind: "ADD" }); }, sx: { flexShrink: 0 }, children: t("messagingAccounts.list.add") }))] }), loading && accounts.length === 0 ? (_jsx(CircularProgress, { "aria-label": t("messagingAccounts.list.loading") })) : error ? (_jsx(Typography, { color: "error", children: t("messagingAccounts.list.loadError") })) : accounts.length === 0 ? (_jsx(Typography, { variant: "body1", children: t("messagingAccounts.list.empty") })) : (_jsx(TableContainer, { children: _jsxs(Table, { size: "small", "aria-label": t("messagingAccounts.list.title"), children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("messagingAccounts.column.channel") }), _jsx(TableCell, { children: t("messagingAccounts.column.name") }), _jsx(TableCell, { children: t("messagingAccounts.column.sender") }), _jsx(TableCell, { children: t("messagingAccounts.column.provider") }), _jsx(TableCell, { children: t("messagingAccounts.column.default") }), _jsx(TableCell, { children: t("messaging.readiness.connected") }), MESSAGE_PURPOSES.map(function (purpose) { return (_jsx(TableCell, { children: purposeLabel(purpose) }, purpose)); }), _jsx(TableCell, { children: t("messagingAccounts.column.lastCheck") }), _jsx(TableCell, { children: t("messagingAccounts.column.actions") })] }) }), _jsx(TableBody, { children: accounts.map(function (account) {
                                var _a, _b, _c;
                                var readiness = accountReadiness(account);
                                var unsupported = accountUnsupported(account);
                                return (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(ChannelLabel, { channel: account.channel, label: t("messaging.channel.".concat(account.channel)) }) }), _jsx(TableCell, { children: account.name }), _jsx(TableCell, { children: (_a = accountSenderLabel(account.sender)) !== null && _a !== void 0 ? _a : "–" }), _jsx(TableCell, { children: t("messaging.provider.".concat(account.provider)) }), _jsx(TableCell, { children: account.is_default ? (_jsx(CheckCircleOutlineIcon, { color: "success", titleAccess: t("messagingAccounts.column.isDefault") })) : null }), _jsx(TableCell, { children: unsupported ? (_jsx(ReadinessCell, { ready: false, label: t("messaging.readiness.notConnected"), reasons: [
                                                    t("messaging.blocker.UNSUPPORTED_PROVIDER"),
                                                ] })) : (_jsx(ReadinessCell, { ready: readiness.connected, label: readiness.connected
                                                    ? t("messaging.readiness.connected")
                                                    : t("messaging.readiness.notConnected"), reasons: checkedForAnotherIdentity(account)
                                                    ? [t("messaging.blocker.UNCHECKED")]
                                                    : !readiness.connected &&
                                                        ((_b = account.status) === null || _b === void 0 ? void 0 : _b.reason)
                                                        ? [
                                                            reasonText(t, account.status.reason),
                                                        ]
                                                        : [] })) }), MESSAGE_PURPOSES.map(function (purpose) {
                                            var blockers = readiness.purposes[purpose].blockers;
                                            return (_jsx(TableCell, { children: _jsx(ReadinessCell, { ready: blockers.length === 0, label: blockers.length === 0
                                                        ? purposeLabel(purpose)
                                                        : t("messaging.readiness.notReady"), reasons: blockerLabels(blockers) }) }, purpose));
                                        }), _jsx(TableCell, { children: _jsx(Typography, { variant: "caption", color: "text.secondary", children: ((_c = account.status) === null || _c === void 0 ? void 0 : _c.checked_at)
                                                    ? t("messaging.readiness.lastCheck", {
                                                        date: formatDate(account.status.checked_at, i18n.language),
                                                    })
                                                    : t("messaging.readiness.neverChecked") }) }), _jsxs(TableCell, { sx: { whiteSpace: "nowrap" }, children: [_jsx(Tooltip, { title: canWrite
                                                        ? t("messagingAccounts.action.edit")
                                                        : t("messagingAccounts.action.view"), children: _jsx(IconButton, { "aria-label": canWrite
                                                            ? t("messagingAccounts.action.editNamed", { name: account.name })
                                                            : t("messagingAccounts.action.viewNamed", { name: account.name }), onClick: function () {
                                                            return setDrawer({ kind: "EDIT", id: account.id });
                                                        }, children: canWrite ? _jsx(EditIcon, {}) : _jsx(VisibilityIcon, {}) }) }), canWrite && (_jsxs(_Fragment, { children: [unsupported ? null : (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: t("messagingAccounts.action.check"), children: _jsx("span", { children: _jsx(IconButton, { "aria-label": t("messagingAccounts.action.checkNamed", {
                                                                                name: account.name,
                                                                            }), disabled: checking === account.id, onClick: function () {
                                                                                return check(account);
                                                                            }, children: _jsx(SyncIcon, {}) }) }) }), _jsx(Tooltip, { title: t("messagingAccounts.action.test"), children: _jsx(IconButton, { "aria-label": t("messagingAccounts.action.testNamed", {
                                                                            name: account.name,
                                                                        }), onClick: function () {
                                                                            return setTesting(account);
                                                                        }, children: _jsx(SendIcon, {}) }) })] })), _jsx(Tooltip, { title: t("messagingAccounts.action.delete"), children: _jsx(IconButton, { "aria-label": t("messagingAccounts.action.deleteNamed", {
                                                                    name: account.name,
                                                                }), onClick: function () { return setDeleting(account); }, children: _jsx(DeleteIcon, {}) }) })] }))] })] }, account.id));
                            }) })] }) })), _jsxs(Drawer, { anchor: "right", open: drawer.kind === "ADD" || !!drawerAccount, onClose: function () { return setDrawer({ kind: "CLOSED" }); }, slotProps: {
                    paper: {
                        "aria-labelledby": MESSAGING_ACCOUNT_EDITOR_TITLE,
                        "sx": { width: { xs: "100%", md: "44%" }, minWidth: { md: 560 } },
                    },
                }, children: [drawer.kind === "ADD" ? (_jsx(MessagingAccountEditor, { canWrite: canWrite, onChanged: reload, onCreated: created, onClose: function () { return setDrawer({ kind: "CLOSED" }); } }, "new")) : null, drawerAccount ? (_jsx(MessagingAccountEditor, { account: drawerAccount, canWrite: canWrite, initialCallback: drawer.kind === "CREATED" ? drawer.callback : undefined, onChanged: reload, onClose: function () { return setDrawer({ kind: "CLOSED" }); } }, drawerAccount.id)) : null] }), testing && (_jsx(MessagingTestDialog, { account: testing, languages: languages, onClose: function () { return setTesting(null); } })), _jsx(Dialog, { variant: "warning", open: !!deleting, ok: String(t("common.label.delete")), cancel: String(t("common.label.cancel")), title: String(t("messagingAccounts.delete.title")), handleClose: function (confirmed) {
                    if (confirmed && deleting) {
                        remove(deleting);
                    }
                    setDeleting(null);
                }, children: t("messagingAccounts.delete.body", { name: (_d = deleting === null || deleting === void 0 ? void 0 : deleting.name) !== null && _d !== void 0 ? _d : "" }) })] }));
};
/**
 * Settings > Messaging: the accounts and their monthly volumes, and the Sent
 * messages tab where the dev fake provider routes answer (`MessagingTabs`).
 */
export var SettingsMessaging = function (_a) {
    var sentMessagesRefreshMs = _a.sentMessagesRefreshMs;
    return (_jsx(MessagingTabs, { accounts: _jsxs(_Fragment, { children: [_jsx(MessagingAccountList, {}), _jsx(MessagingMonthlyVolumes, {})] }), refreshMs: sentMessagesRefreshMs }));
};
